import React, { useEffect, useState } from 'react';
import { Form, Formik } from 'formik';
import { eJournalEntryPaymentMethod } from '../../types/accounting/eJournalEntryPaymentMethod';
import { eJournalEntryType } from '../../types/accounting/eJournalEntryType';
import { EditJournalEntryModal } from './EditJournalEntryModal';
import { newJournalEntrySchema } from '../../store/accounting/newJournalEntrySchema';
import { useAsyncEffect, Header3Text, Header4Text } from 'ui-toolkit';
import { IEscrowJournalEntryData } from '../../types/accounting/data/IEscrowJournalEntryData';
import { getOrdersSearchAction } from '../../store/orders';
import { getJournalEntryByIdAsync } from '../../API/accounting/getJournalEntryByIdAsync';
import { updateJournalEntryAsync } from '../../API/accounting/updateJournalEntryAsync';
import { IOrderOption } from '../../types/accounting/IOrderOption';
import {
  createDefaultFetch,
  createFetchedSuccess,
  createFetchedWithError,
  createFetchInProgress,
} from 'types/rest/IFetchStatus';
import { eSearchOrdersOrderBy } from '../../ui-toolkit/orders/searchOrder/types/eSearchOrdersOrderBy';
import { useAppDispatch } from '../../store/store';

export interface ISavedJournalEntryFormValues {
  paymentMethod?: eJournalEntryPaymentMethod;
  orderId?: string;
  amountCents?: number;
  entryType?: eJournalEntryType;
  note?: string;
  initiatedAtDate?: string;
  completedAtDate?: string;
}

export interface IEditJournalEntryProps {
  onClose: () => void;
  onSuccess: () => void;
  journalEntryId: string;
}

export const EditJournalEntryModalContainer: React.FC<IEditJournalEntryProps> = ({
  onClose,
  onSuccess,
  journalEntryId,
}) => {
  const [loadingStatus, setLoadingStatus] = useState(createDefaultFetch());
  const [updateStatus, setUpdateStatus] = useState(createDefaultFetch());
  const [journalEntry, setJournalEntry] = useState<IEscrowJournalEntryData | undefined>();
  const [order, setOrder] = useState<IOrderOption | undefined>(undefined);
  const dispatch = useAppDispatch();

  useAsyncEffect(async () => {
    setLoadingStatus(createFetchInProgress());
    try {
      const journalEntry: IEscrowJournalEntryData = await getJournalEntryByIdAsync(journalEntryId);
      setJournalEntry(journalEntry);
      setLoadingStatus(createFetchedSuccess());
    } catch (fetchError) {
      setLoadingStatus(createFetchedWithError());
    }
  }, [journalEntryId]);

  useEffect(() => {
    if (journalEntry) {
      dispatch(
        getOrdersSearchAction({
          fileNumbers: [journalEntry.escrowFile.order.fileNumber],
          sortBy: eSearchOrdersOrderBy.createdAtDESC,
          pageNumber: 0,
          pageSize: 5,
        })
      );
    }
  }, [journalEntry, dispatch]);

  const updateJournalEntry = async (values: ISavedJournalEntryFormValues) => {
    setUpdateStatus(createFetchInProgress());
    try {
      await updateJournalEntryAsync(journalEntry!.id, {
        amountCents: values.amountCents!,
        entryType: values.entryType!,
        note: values.note!,
        paymentMethod: values.paymentMethod!,
        initiatedAtDate: values.initiatedAtDate!,
        completedAtDate: values.completedAtDate,
      });
      setUpdateStatus(createFetchedSuccess());
    } catch (fetchError) {
      setUpdateStatus(createFetchedWithError());
    }
  };

  const loadingTitle = loadingStatus.success ? 'Saving Journal Entry...' : 'Loading Journal Entry...';
  const fetchStatus = loadingStatus.success ? updateStatus : loadingStatus;

  return (
    <Formik
      enableReinitialize
      validateOnMount
      validationSchema={newJournalEntrySchema}
      initialValues={
        journalEntry
          ? ({
              paymentMethod: journalEntry.paymentMethod,
              orderId: journalEntry.escrowFile.order.id,
              amountCents: journalEntry.amountCents,
              entryType: journalEntry.entryType,
              note: journalEntry.note,
              initiatedAtDate: journalEntry.initiatedAtDate,
              completedAtDate: journalEntry.completedAtDate,
            } as ISavedJournalEntryFormValues)
          : {}
      }
      onSubmit={updateJournalEntry}
    >
      {({ isValid, submitForm }) => (
        <Form>
          <EditJournalEntryModal
            title={<Header4Text>Edit Journal Entry</Header4Text>}
            errorTitle={<Header3Text>Error saving new Journal Entry</Header3Text>}
            loadingTitle={loadingTitle}
            successTitle="Journal Entry was saved."
            saveButtonText="Save"
            disabledFields={['escrowFileId']}
            escrowFile={journalEntry ? journalEntry.escrowFile : undefined}
            fetchStatus={fetchStatus}
            isValid={isValid}
            onClose={onClose}
            onSuccess={onSuccess}
            onOrderSelected={setOrder}
            selectedOrder={order}
            submitForm={submitForm}
          />
        </Form>
      )}
    </Formik>
  );
};
